import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import AddMemberToSample from '@components/assign-team-member/AddMemberToSample';
import SampleColorPopUp from '@components/colors/SampleColorPopUp';
import SampleComment from '@components/SampleComment';
import { toastError, toastSuccess } from '@components/Toast';
import TopDetails from '@components/TopDetails';

import RequestTimeline from '@features/sampleDevelopment/components/RequestTimeline';
import SampleInlineComment from '@features/sampleDevelopment/components/SampleInlineComment';

import { clearCurrentActivePage, setCurrentActivePage } from '@store/actions/ActivePageAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import {
	_getKey,
	changeDateFormat,
	checkSingular,
	getSampleStatus,
	packTag,
	renderSetImages,
	setTag,
	STATIC_SET_CONSTANTS
} from '@utils/Util';

const SingleSampleItem = ({ sampleData, setSampleInfo, fetchSampleDetails }) => {
	const [activeRemarksId, setActiveRemarksId] = useState(null);
	const [showComment, setShowComment] = useState(false);
	const [designId, setDesignId] = useState(null);
	const [errors, setErrors] = useState([]);
	const [selectedId, setSelectedId] = useState(null);
	const [sample, setSample] = useState(null);
	const [isApproveBtnDisabled, setIsApproveBtnDisabled] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();

	useEffect(() => {
		if (sample?.approvalStatus === 'APPROVED') {
			navigate(`/sample/view/${sample?.id}`, { replace: true });
		}
	}, [sample?.approvalStatus]);

	useEffect(() => {
		setSample(sampleData);
		return () => {
			dispatch(clearCurrentActivePage());
		};
	}, [sampleData]);

	const getFomattedData = () => {
		let selectedDesignArr = [];
		sample?.sampleRequestItemResponseList?.forEach((item) => {
			if (isProductHasSet(item)) {
				return item?.setMemberSampleRequestItemResponseList.forEach((member) => {
					if (member.id === designId) {
						selectedDesignArr.push(member);
					}
				});
			} else if (item.id === designId) {
				selectedDesignArr.push(item);
			}
		});

		return (
			selectedDesignArr.length &&
			selectedDesignArr[0]?.sampleItemDetailsResponseList.map((itemDetailsId) => {
				return {
					colorDTO: {
						name: itemDetailsId.code,
						colorType: 'SOLID',
						representedBy: 'PANTONE_OR_HEX_CODE',
						compositeColorList: [],
						pantoneColorId: itemDetailsId.pantoneColorId,
						swatchDocumentId: null
					},
					key: itemDetailsId.key,
					colorId: itemDetailsId?.colorResponse?.id,
					itemDetailsId: itemDetailsId?.id,
					quantity: itemDetailsId.quantity || null,
					size: itemDetailsId.size || null
				};
			})
		);
	};

	const isValidAtleastOneRow = (colors) => {
		let isValid = false;
		colors.forEach((item) => {
			if (item.quantity && item.size) {
				isValid = true;
			}
		});
		return isValid;
	};

	const isValidForm = (body) => {
		setErrors([]);
		let isValid = true;
		body.forEach((item) => {
			if (item?.colorDTO?.name && !item.size) {
				setErrors((old) => [
					...old,
					{
						errorKey: item.key,
						type: 'SIZE'
					}
				]);
				isValid = false;
			}
			if (item?.colorDTO?.name && !item.quantity) {
				setErrors((old) => [
					...old,
					{
						errorKey: item.key,
						type: 'QUANTITY'
					}
				]);
				isValid = false;
			}
			if (item.quantity && !item.size) {
				setErrors((old) => [
					...old,
					{
						errorKey: item.key,
						type: 'SIZE'
					}
				]);
				isValid = false;
			}
			if (item.size && !item.quantity) {
				setErrors((old) => [
					...old,
					{
						errorKey: item.key,
						type: 'QUANTITY'
					}
				]);
				isValid = false;
			}
		});
		if (!isValidAtleastOneRow(body)) {
			setErrors((old) => [
				...old,
				{
					errorKey: body[0].key,
					type: 'SIZE'
				}
			]);
			setErrors((old) => [
				...old,
				{
					errorKey: body[0].key,
					type: 'QUANTITY'
				}
			]);
			isValid = false;
		}
		return isValid;
	};

	const onSubmitColorSizeQuantity = async (body) => {
		let quantityList = body?.map((item) => {
			{
				delete item.key;
				return item;
			}
		});
		dispatch(openLoader());
		await ApiClient.PUT('sampleDetailsAction', quantityList, `${sample.id}/items/${designId}`)
			.then(({ data }) => {
				setDesignId(null);
				toastSuccess(data.message);
				fetchSampleDetails();
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	};

	const onAddColorSizeQuantity = (e) => {
		e.stopPropagation();
		let formattedInputs = getFomattedData();
		const isValid = isValidForm(formattedInputs);

		if (!isValid) return;
		onSubmitColorSizeQuantity(formattedInputs);
	};

	const onApprove = async () => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('sampleDetailsAction', {}, `${sample.id}/approve`);
			dispatch(closeLoader());
			if (data) {
				navigate(`/sample/view/${sample.id}`, { replace: true });
			}
		} catch ({ response }) {
			dispatch(closeLoader());
			if (response && response.data && response.data.message) {
				toastError(response.data.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const onCancel = async () => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('sampleDetailsAction', {}, `${sample.id}/cancel`);
			dispatch(closeLoader());
			if (data) {
				navigate('/buyer-requests');
			}
		} catch ({ response }) {
			dispatch(closeLoader());
			if (response && response.data && response.data.message) {
				toastError(response.data.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const onDesignClick = (id) => {
		setDesignId(id);
	};

	const onChangeColorItem = (e, colorItem) => {
		let { name, value } = e.target;
		let updatedDesigns = sample?.sampleRequestItemResponseList;
		updatedDesigns.map((item) => {
			if (isProductHasSet(item)) {
				return item?.setMemberSampleRequestItemResponseList.map((member) => {
					if (member.id === designId) {
						return member.sampleItemDetailsResponseList.map((color) => {
							if (color.key === parseInt(colorItem.key)) {
								if (name === 'code' && !value) {
									color[name] = value;
									color['hexCode'] = null;
									color['pantoneColorId'] = null;
								}
								color[name] = value;
							}
							return color;
						});
					}
				});
			} else {
				if (item.id === designId) {
					item.sampleItemDetailsResponseList.map((color) => {
						if (color.key === parseInt(colorItem.key)) {
							if (name === 'code' && !value) {
								color[name] = value;
								color['hexCode'] = null;
								color['pantoneColorId'] = null;
							}
							color[name] = value;
						}
						return color;
					});
				}
			}
		});
		setSampleInfo({ ...sample, sampleRequestItemResponseList: [...updatedDesigns] });
	};

	const onClickAddNew = (color, key) => {
		let updatedDesigns = sample?.sampleRequestItemResponseList;
		updatedDesigns.map((item) => {
			if (isProductHasSet(item)) {
				item.setMemberSampleRequestItemResponseList.map((member) => {
					if (member?.id === designId) {
						return member.sampleItemDetailsResponseList.map((existingColor) => {
							if (existingColor.key === parseInt(key)) {
								existingColor['code'] = color.code;
								existingColor['hexCode'] = color.hexCode;
								existingColor['pantoneColorId'] = color.pantoneColorId;
							}
							return existingColor;
						});
					}
				});
			} else {
				if (item.id === designId) {
					item.sampleItemDetailsResponseList.map((existingColor) => {
						if (existingColor.key === parseInt(key)) {
							existingColor['code'] = color.code;
							existingColor['hexCode'] = color.hexCode;
							existingColor['pantoneColorId'] = color.pantoneColorId;
						}
						return existingColor;
					});
				}
			}
		});
		setSampleInfo({ ...sample, sampleRequestItemResponseList: [...updatedDesigns] });
	};

	const renderColorAdd = (item) => {
		return (
			<div>
				<SampleColorPopUp
					colorList={item?.sampleItemDetailsResponseList}
					selectedDesignId={designId}
					item={item}
					onCancel={(e) => {
						e.stopPropagation();
						setDesignId(null);
					}}
					onChangeColorItem={onChangeColorItem}
					onClickAddNewItem={onClickAddNew}
					onClickColorSubmit={(e) => {
						onAddColorSizeQuantity(e);
					}}
					errors={errors}
				/>
			</div>
		);
	};

	const onCopyDesign = async (designId) => {
		dispatch(openLoader());
		await ApiClient.POST('sampleDetailsAction', {}, `${sample.id}/items/${designId}/copy-product`)
			.then(({ data }) => {
				toastSuccess(data.message);
				fetchSampleDetails();
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	};

	const onClearChat = () => {
		setSample((prevQuoteStore) => ({
			...prevQuoteStore,
			sampleRequestItemResponseList: prevQuoteStore?.sampleRequestItemResponseList?.map((item) => {
				if (item.productId === selectedId) {
					return {
						...item,
						noOfUnreadMessage: 0
					};
				}
				return item;
			})
		}));

		setSelectedId(null);
	};

	const handleCommentClose = () => {
		dispatch(clearCurrentActivePage());
		setShowComment(false);
		onClearChat();
	};

	const isProductHasSet = (product) => {
		return product.isSet || product.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT;
	};

	const isProductHasPack = (product) => {
		return product.isPack || product.setPackStyleFlag === STATIC_SET_CONSTANTS.PACK_PRODUCT;
	};

	const renderSampleColors = (responseColors = []) => {
		return (
			<div className="sample-details">
				{responseColors.length > 0 &&
					responseColors?.map((size) => (
						<ul key={size.id} className="common-list-component align-items-center mb-2">
							<li className="d-flex align-items-center">
								{size?.colorResponse?.hexCode ? (
									<span
										className="color-icon"
										style={{
											background: size?.colorResponse?.hexCode
										}}
									/>
								) : (
									<span></span>
								)}
								<span className="ml-1">
									{size?.colorResponse?.code || size?.colorResponse?.name}
								</span>
							</li>
							{size.size && size.quantity && (
								<li>
									{size.size} - {checkSingular('PCS', size.quantity)}
								</li>
							)}
						</ul>
					))}
			</div>
		);
	};

	return (
		<div>
			<div className="top-header bg-white">
				<div className="filter-container-top pt-2 pb-3 pr-0 bg-gray pl-0">
					<div className="filters">
						<div className="sample-full-status">
							<ul className="common-list-component mb-2">
								<li>
									<img
										src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`}
										alt="back"
										onClick={() => navigate(-1)}
										className="cursor-pointer"
									/>
									<span className="gray_dark_01">
										{sample?.title || sample?.collectionName}{' '}
									</span>
								</li>

								<li>
									<span className="gray_dark_01">{sample?.refNo} </span>
								</li>
								<li>
									<span className="gray_dark_01">{sample?.requestedBy} </span>
								</li>
								<li>
									<span>
										{changeDateFormat(sample?.requestedDate, 'YYYY-MM-DD', 'Do MMM, YY')}
									</span>
								</li>
								<li>
									<span>{checkSingular('Design', sample?.noOfDesign)} </span>
								</li>
								<li>
									<span className="brand-common-design">{sample?.brand} </span>
								</li>
							</ul>
						</div>
					</div>
					<div className="d-flex align-items-center">
						<AddMemberToSample
							sampleId={sample?.id}
							toggleBtnDisabled={setIsApproveBtnDisabled}
							requestFrom="PENDING_REQUEST"
						/>

						{sample?.approvalStatus !== 'CANCELLED' && sample?.approvalStatus !== 'APPROVED' && (
							<div className="ml-3">
								<button
									className="button gray-outline size32 mr-3"
									onClick={() => onCancel()}
								>
									<span>Cancel</span>
								</button>
								<button
									disabled={isApproveBtnDisabled}
									className="button approve size32"
									onClick={() => onApprove()}
								>
									<span>Approve</span>
								</button>
							</div>
						)}

						{/* <div className="conversation-button">
							<button className="button text size32" onClick={() => setShowComment(true)}>
								<img src={`${process.env.PUBLIC_URL}/icons/comments-gray.svg`} alt="comment" />
							</button>
						</div> */}
					</div>
				</div>
			</div>
			<div className="sample-request-gorup mt-0">
				<div className="sample-details-view-table bg-gray">
					<TopDetails
						type={'SAMPLE'}
						description={sample?.description}
						documentLists={sample?.requestedDocumentList}
					/>
					<div>
						<table className="table with-border scroll-x-label scroll-y-label">
							<thead>
								<tr>
									<th>Design</th>
									<th>Material</th>
									<th className="text-nowrap">Fabric color, size &amp; qty</th>
									<th>Notes</th>
									<th>Status &amp; date</th>
									<th>Conversation</th>
								</tr>
							</thead>
							<tbody>
								{sample?.sampleRequestItemResponseList?.map((item) => (
									<>
										<tr key={item?.productId}>
											<td
												rowSpan={
													isProductHasSet(item)
														? item?.setMemberSampleRequestItemResponseList.length
														: 1
												}
											>
												<div className="d-flex">
													<div className="design-image">
														{isProductHasSet(item) ? (
															renderSetImages(
																item?.setMemberSampleRequestItemResponseList,
																'56px'
															)
														) : (
															<img
																src={item?.featureImageDocUrl}
																alt="image"
																className="cell-img pr-2"
															/>
														)}

														{!isProductHasSet(item) && (
															<img
																onClick={() => onCopyDesign(item.id)}
																src={`${process.env.PUBLIC_URL}/icons/copy-32-dark`}
																className="action-icon"
															></img>
														)}
													</div>
													<div className="d-inline-block align-middle ml-1">
														<div className="mb-1">
															{isProductHasPack(item) && packTag()}
															{isProductHasSet(item) && setTag()}
														</div>
														<span className="text-nowrap">
															{item?.referenceNumber}
														</span>
														<br />
														<ul className="common-list-component my-1">
															{item?.market && <li>{item?.market}</li>}
															{isProductHasSet(item) ? (
																item.setMemberSampleRequestItemResponseList.map(
																	(member) => (
																		<li key={member?.id}>
																			{member?.category}{' '}
																		</li>
																	)
																)
															) : (
																<li>{item?.category} </li>
															)}
														</ul>
														<ul className="common-list-component">
															{isProductHasSet(item) ? (
																<li>
																	{item.setMemberSampleRequestItemResponseList &&
																		item
																			.setMemberSampleRequestItemResponseList[0]
																			?.productCreator}
																</li>
															) : (
																<li>{item?.productCreator}</li>
															)}
															{isProductHasSet(item) ? (
																<li>
																	{changeDateFormat(
																		item.setMemberSampleRequestItemResponseList &&
																			item
																				.setMemberSampleRequestItemResponseList[0]
																				?.productCreationDate,
																		'DD-MM-YYYY',
																		'Do MMM, YY'
																	)}
																</li>
															) : (
																<li>
																	{changeDateFormat(
																		item?.productCreationDate,
																		'DD-MM-YYYY',
																		'Do MMM, YY'
																	)}
																</li>
															)}
														</ul>
													</div>
												</div>
											</td>
											<td>
												<ul>
													{isProductHasSet(item) &&
													item?.setMemberSampleRequestItemResponseList &&
													item?.setMemberSampleRequestItemResponseList[0]
														?.materialList
														? item?.setMemberSampleRequestItemResponseList[0]?.materialList.map(
																(material) => (
																	<li key={_getKey()}>{material}</li>
																)
															)
														: item?.materialList &&
															item?.materialList.map((material) => (
																<li key={_getKey()}>{material}</li>
															))}
												</ul>
											</td>
											<td
												onClick={() =>
													onDesignClick(
														isProductHasSet(item)
															? item?.setMemberSampleRequestItemResponseList[0]
																	?.id
															: item?.id
													)
												}
											>
												{isProductHasSet(item)
													? designId ===
															item?.setMemberSampleRequestItemResponseList[0]
																?.id &&
														renderColorAdd(
															item?.setMemberSampleRequestItemResponseList[0]
														)
													: designId === item.id && renderColorAdd(item)}
												{isProductHasSet(item)
													? renderSampleColors(
															item?.setMemberSampleRequestItemResponseList[0]
																?.sampleItemDetailsResponseList
														)
													: renderSampleColors(item?.sampleItemDetailsResponseList)}
											</td>
											<td
												onClick={() => item.id && setActiveRemarksId(item.id)}
												rowSpan={
													isProductHasSet(item)
														? item?.setMemberSampleRequestItemResponseList.length
														: 1
												}
											>
												<SampleInlineComment
													sampleRequestId={sample.id || 0}
													id={item.id}
													remarks={item?.remarks}
													activeRemarksId={activeRemarksId}
													onReset={() => {
														setActiveRemarksId(null);
													}}
												/>
											</td>
											<td>
												<div className="sample-status-with-details">
													<div className="sample-status">
														{isProductHasSet(item)
															? getSampleStatus(
																	item
																		?.setMemberSampleRequestItemResponseList[0]
																		?.currentPendingActivity?.activityType
																)
															: getSampleStatus(
																	item?.currentPendingActivity?.activityType
																)}

														{item.basePrice && (
															<p className="costing-amount regular-14">
																${item.basePrice?.toFixed(2)}{' '}
																<span>/unit</span>
															</p>
														)}
														{item?.consumptionResponse &&
															item.consumptionResponse.map((res) => (
																<ul
																	className="common-list-component"
																	key={_getKey()}
																>
																	<li>
																		{res.quantity}
																		{res.quantityUnit}
																	</li>
																</ul>
															))}
													</div>
													<p className="regular-14 weight-300 gray_dark_02">
														{item.requireDate && (
															<>
																<span>RQD</span>
																<span className="gray_dark_02 ml-2">
																	{changeDateFormat(
																		item.requireDate,
																		'YYYY-MM-DD',
																		'Do MMM, YY'
																	)}
																</span>
															</>
														)}
													</p>

													{item.id && (
														<RequestTimeline
															requireDate={item?.requireDate}
															currentActivity={
																item?.currentPendingActivity?.activityType ||
																null
															}
															activityList={
																isProductHasSet(item)
																	? item
																			?.setMemberSampleRequestItemResponseList[0]
																			?.timeLineActivityResponse
																	: item.timeLineActivityResponse
															}
														/>
													)}
												</div>
											</td>
											<td
												rowSpan={
													isProductHasSet(item)
														? item?.setMemberSampleRequestItemResponseList.length
														: 1
												}
											>
												<div className="conversation-btn">
													<button
														className="button text"
														onClick={() => {
															dispatch(
																setCurrentActivePage({
																	id: item?.productId,
																	type: 'PRODUCT_CONVERSATION',
																	pageType: 'DETAILS'
																})
															);
															setSelectedId(item?.productId);
															setShowComment(true);
														}}
													>
														{item?.noOfUnreadMessage > 0 ? (
															<img
																src={`${process.env.PUBLIC_URL}/icons/comment-not-read.svg`}
																alt="not read"
															/>
														) : (
															<img
																src={`${process.env.PUBLIC_URL}/icons/comments-gray.svg`}
																alt="not read"
															/>
														)}
													</button>
												</div>
											</td>
										</tr>
										{isProductHasSet(item) &&
											item?.setMemberSampleRequestItemResponseList &&
											item?.setMemberSampleRequestItemResponseList
												.slice(1)
												.map((member) => (
													<tr key={member?.id}>
														<td>
															{member?.materialList &&
																member.materialList.map((material) => (
																	<p className="mb-1" key={_getKey()}>
																		{material}
																	</p>
																))}
														</td>

														<td onClick={() => onDesignClick(member?.id)}>
															{designId === member?.id &&
																renderColorAdd(member)}
															{renderSampleColors(
																member?.sampleItemDetailsResponseList
															)}
														</td>
														<td>
															<div className="sample-status-with-details">
																<div className="sample-status">
																	{getSampleStatus(
																		item?.currentPendingActivity
																			?.activityType
																	)}
																	<p className="regular-14 weight-300 gray_dark_02">
																		{item.requireDate && (
																			<>
																				<span>RQD</span>
																				<span className="gray_dark_02 ml-2">
																					{changeDateFormat(
																						item.requireDate,
																						'YYYY-MM-DD',
																						'Do MMM, YY'
																					)}
																				</span>
																			</>
																		)}
																	</p>
																</div>
																{item.id && (
																	<RequestTimeline
																		requireDate={member?.requireDate}
																		currentActivity={
																			member?.currentPendingActivity
																				?.activityType || null
																		}
																		activityList={
																			member.timeLineActivityResponse
																		}
																	/>
																)}
															</div>
														</td>
													</tr>
												))}
									</>
								))}
							</tbody>
						</table>
					</div>
				</div>

				{selectedId && (
					<SampleComment
						sampleRequestId={id}
						postTypeFilter={'PRODUCT_DEVELOPMENT_COMMENT'}
						topic={'PRODUCT_SAMPLE_COMMENT'}
						postTypeId={'sampleRequestId'}
						productId={selectedId}
						showComment={showComment}
						setShowComment={handleCommentClose}
						brandInfo={{
							brandId: sample?.brandId,
							brandName: sample?.brand
						}}
					/>
				)}
			</div>
		</div>
	);
};

export default SingleSampleItem;
